import { useState, useEffect } from 'react';
import { getTarifByCode } from '../../../services/tarifsService';

/**
 * Le prix affiché, formaté selon la langue.
 *
 * Intl fait le travail : virgule décimale en français, point en anglais,
 * position du symbole selon la locale. Recopier un format à la main
 * produirait « 9.99 € » en français.
 */
function formaterPrix(montant, langue) {
    const n = Number(montant);
    if (!Number.isFinite(n)) return null;

    try {
        return new Intl.NumberFormat(langue || 'fr', {
            style: 'currency',
            currency: 'EUR'
        }).format(n);
    } catch (e) {
        // Locale inconnue : le montant brut vaut mieux qu'un écran vide.
        return `${n.toFixed(2)} €`;
    }
}

/**
 * Le nom du tarif dans la langue courante.
 */
function nomDuTarif(tarif, langue) {
    if (!tarif) return '';
    const cles = [`name_${langue}`, `NAME_${String(langue).toUpperCase()}`, 'name_en', 'NAME_EN', 'code', 'CODE'];
    for (const c of cles) {
        const v = tarif[c];
        if (v && String(v).trim()) return String(v).trim();
    }
    return '';
}

/**
 * Le bloc qui affiche un prix — LE SEUL QUI LIT LA BASE.
 *
 * Le montant n'est jamais saisi dans le bloc. Recopié, il se dupliquerait
 * en jusqu'à 168 exemplaires — vingt et une pages fois huit langues — et
 * deviendrait faux silencieusement le jour où un tarif change. Sur un site
 * belge, un prix affiché faux engage juridiquement.
 *
 * Deux bénéfices, en plus de l'exactitude : le formatage monétaire s'adapte
 * à la langue, et le prix sort entièrement du périmètre de traduction.
 *
 * `codeTarif` vise un tarif PAR CODE : aussi bien une formule globale
 * qu'une application autonome.
 *
 * Tarif introuvable : on n'affiche RIEN. Jamais un prix de repli, jamais un
 * zéro — un prix faux est pire qu'un prix absent.
 */
const TarifBloc = ({ bloc, langue = 'fr' }) => {
    const [tarif, setTarif] = useState(null);
    const [chargement, setChargement] = useState(true);

    const code = bloc?.codeTarif || bloc?.codetarif || '';

    useEffect(() => {
        if (!code) {
            setChargement(false);
            return;
        }

        let annule = false;
        getTarifByCode(code)
            .then((t) => { if (!annule) setTarif(t); })
            .catch(() => { if (!annule) setTarif(null); })
            .finally(() => { if (!annule) setChargement(false); });

        return () => { annule = true; };
    }, [code]);

    if (chargement) {
        return (
            <section className="mb-10" data-testid="bloc-tarif-chargement">
                <div className="h-32 rounded-2xl bg-gray-100 animate-pulse" />
            </section>
        );
    }

    // Rien plutôt qu'un prix faux.
    if (!tarif) return null;

    const prixMensuel = formaterPrix(
        tarif.monthlyprice ?? tarif.monthlyPrice ?? tarif.MONTHLYPRICE,
        langue
    );
    if (prixMensuel === null) return null;

    const prixAnnuel = formaterPrix(
        tarif.yearlyprice ?? tarif.yearlyPrice ?? tarif.YEARLYPRICE,
        langue
    );
    const nom = nomDuTarif(tarif, langue);

    return (
        <section className="mb-10" data-testid="bloc-tarif">
            <div className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-6 text-center">
                {bloc?.titre && (
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{bloc.titre}</h3>
                )}
                {!bloc?.titre && nom && (
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{nom}</h3>
                )}

                <p className="text-4xl font-bold text-gray-900 tabular-nums my-3">
                    {prixMensuel}
                    {bloc?.periode && (
                        <span className="text-base font-normal text-gray-500"> / {bloc.periode}</span>
                    )}
                </p>

                {prixAnnuel && bloc?.mentionAnnuelle && (
                    <p className="text-sm text-gray-500 mb-4">
                        {bloc.mentionAnnuelle} {prixAnnuel}
                    </p>
                )}

                {bloc?.description && (
                    <p className="text-sm text-gray-600 max-w-md mx-auto mb-5">{bloc.description}</p>
                )}

                {bloc?.lienTexte && bloc?.lien && (
                    <a
                        href={bloc.lien}
                        className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-cabaza-purple to-cabaza-pink hover:opacity-90 transition"
                    >
                        {bloc.lienTexte}
                    </a>
                )}
            </div>
        </section>
    );
};

export default TarifBloc;
